import type { RecipeStore } from '@platewell/core';
import {
  IndexedDbBlobSource,
  IndexedDbUploadQueueStore,
  PhotoUploadQueue,
} from '@platewell/sync-client';
import { API_BASE_URL } from './api.js';

export const blobSource = new IndexedDbBlobSource();

export const uploadQueue = new PhotoUploadQueue({
  store: new IndexedDbUploadQueueStore(),
  blobs: blobSource,
  apiBaseUrl: API_BASE_URL,
});

/** Local object URLs for photos still waiting to upload, keyed by blob id. */
const localPreviews = new Map<string, string>();

export function localPreviewUrl(blobId: string): string | null {
  return localPreviews.get(blobId) ?? null;
}

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/avif', 'image/jxl', 'image/png', 'image/webp', 'image/heic']);

export interface AttachResult {
  blobId: string;
  photoId: string;
}

/**
 * Adds a photo to a recipe without waiting for the network.
 *
 * The bytes are hashed and written to the recipe immediately, then handed to the upload
 * queue. That ordering is deliberate: the recipe is complete and syncs to other devices
 * right away, and the pixels catch up whenever the connection does.
 */
export async function attachPhoto(
  store: RecipeStore,
  recipeId: string,
  file: File,
  householdId: string,
): Promise<AttachResult> {
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error(`${file.type || 'that file type'} is not a supported image format`);
  }

  const bytes = new Uint8Array(await file.arrayBuffer());
  const dimensions = await readDimensions(file);
  const localRef = `local:${crypto.randomUUID()}`;
  await blobSource.write(localRef, bytes);

  const blobId = await uploadQueue.enqueue({
    householdId,
    bytes,
    localRef,
    contentType: file.type,
    width: dimensions.width,
    height: dimensions.height,
  });

  localPreviews.set(blobId, URL.createObjectURL(file));

  const photoId = await store.addPhoto(recipeId, {
    blobId,
    width: dimensions.width,
    height: dimensions.height,
  });

  return { blobId, photoId };
}

async function readDimensions(file: File): Promise<{ width: number; height: number }> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    // HEIC and some AVIF files cannot be decoded by every browser. Zero means "unknown",
    // and the server fills real dimensions in when it generates thumbnails.
    return { width: 0, height: 0 };
  }
}
